<script setup lang="ts">
import { computed, ref, h, watch, onMounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { HOTEL_ONLY, STATUS_OPEN, STATUS_CLOSED, LANG_ENGLISH, LANG_FRENCH, INCENTIVE_GROUP_TYPE } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { helpers, required, minValue } from '@vuelidate/validators';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useDestinationStore } from '@/store/DestinationStore';
import { useHotelStore } from '@/store/HotelStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useTabComposable } from './useTabComposable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import SubmitNotificationModal from '@/components/modal/SubmitNotificationModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';
import HotelFormOption from '@/components/tabs/HotelQuoteForm/HotelFormOption.vue';
import { getTextVariant } from '@/utils/stringUtils';

interface Props {
  instanceForm?: any;
  advancedForm: any;
  isValidation: boolean;
  avgPaxRoomData: number;
  selectedLanguage: string;
  quoteIntro: object;
  calendarData?: object;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  submit: [];
  clear: [];
}>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const { getUserParam, getUserQuote, validateDateTime, renderOriginQuote, renderRoomName, getQuoteDepartureData, getLimitDate, cleanEmptyRooms, validateBlackoutDate } = useTabComposable();

const initHotelData = () => {
  return {
    hotel: '',
    rooms: [
      {
        room: ''
      }
    ]
  };
};

const initHotelForm = () => {
  return {
    destination: '',
    dateRange: [],
    duration: '',
    adults: 0,
    children: 0,
    numOfRooms: 0,
    hotels: [initHotelData()],
    additionalNote: '',
    isReturn: false
  };
};

const hotels = ref([initHotelForm()]);

const rules = {
  $each: helpers.forEach({
    destination: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    dateRange: {
      required: helpers.withMessage(t('validate.required'), required)
    },
    adults: {
      required: helpers.withMessage(t('validate.required'), required),
      minValue: helpers.withMessage(t('validate.min_value'), minValue(1))
    },
    numOfRooms: {
      required: helpers.withMessage(t('validate.required'), required),
      minValue: helpers.withMessage(t('validate.min_value'), minValue(1))
    },
    hotels: {
      $each: helpers.forEach({
        hotel: { required: helpers.withMessage(t('validate.required'), required) },
        rooms: {
          $each: helpers.forEach({
            room: { required: helpers.withMessage(t('validate.required'), required) }
          })
        }
      })
    }
  })
};

const currentLanguage = getCurrentLocale();
let v$ = useVuelidate(rules, hotels.value);
const isShowError = ref(false);
const isClearAgencyInfo = ref(false);
const adminSetupStore = useAdminSetupStore();
const destinationStore = useDestinationStore();
const hotelStore = useHotelStore();

const isAdvancedquoteForm = ref(true);
const submitQuoteStore = useSubmitQuoteStore();

const viewQuoteStore = useViewQuoteStore();
const dialog = useDialog();
const minDateRangeValue = ref(new Date(DateTime.utc().plus({ days: 60 })));
const maxDateRangeValue = ref<(Date | undefined)[]>([]);
const limitCalendar = ref({});

const quote = computed(() => viewQuoteStore.selectQuote);
const isLoading = computed(() => submitQuoteStore.selectIsLoading);
const destinationsOnlyHotel = computed(() => {
  return destinationStore.selectDestinationsOnlyHotel;
});

const hotelInstaStatus = ref([STATUS_CLOSED]);
const destinationRequote = computed(() => destinationStore.selectDestinationRequote);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const hotelMinimumConfig = computed(() => adminSetupStore.minimumConfig?.hotelConfig);
const getCommonPayload = () => {
  return {
    gName: isAdvancedquoteForm.value ? props.advancedForm[0].groupName : props.instanceForm[0].groupName,
    gContact: isAdvancedquoteForm.value ? props.advancedForm[0].groupContact : props.instanceForm[0].groupContact,
    gType: isAdvancedquoteForm.value ? props.advancedForm[0].groupType : props.instanceForm[0].groupType,
    gTypeFr: isAdvancedquoteForm.value ? props.advancedForm[0].groupTypeFr : props.instanceForm[0].groupTypeFr,
    gTypeId: isAdvancedquoteForm.value ? props.advancedForm[0].groupTypeId : props.instanceForm[0].groupTypeId
  };
};

const checkPassengerHotels = (hotelList: any) => {
  return hotelList?.map((hotelItem: any) => hotelItem?.adt + hotelItem?.chd).some((total: number) => total < hotelMinimumConfig.value.minPax);
};

const checkNumberOfRoomsHotels = (hotelList: any) => {
  return hotelList?.map((hotelItem: any) => hotelItem?.numOfRooms).some((total: number) => total < hotelMinimumConfig.value.minRoomsCabins);
};

const showModalErrorHotel = () => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: t('notification.over_days')
    },
    props: {
      header: t('common.labels.unable_to_process'),
      modal: true
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { label: t('common.buttons.close'), autofocus: true, onClick: () => modalRef.close() }))];
      }
    }
  });
};

const renderAgentInfo = (isWorksheetRequoteValue: boolean, isClear: boolean) => {
  return isWorksheetRequoteValue && !isClear ? getUserQuote(quote.value) : getUserParam();
};

const showDialog = () => {
  dialog.open(SubmitNotificationModal, {
    props: {
      modal: true,
      header: t('advance_quote.confirm_quote_id')
    }
  });
};

const openModalTimeOut = () => {
  clearAllDataHotel();
  showDialog();
};

const showValidateQuoteFormNotificationHotel = (message: string) => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: message
    },
    props: {
      header: t('advance_quote.form.notice'),
      modal: true,
      style: {
        maxWidth: '90vw'
      }
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.return_to_form'),
              autofocus: true,
              onClick: () => dialogRef.close()
            })
          )
        ];
      }
    }
  });
};

const checkMaxRoomsHotel = async (quoteData: any) => {
  let isValid: boolean = true;
  if (quoteData.gType !== INCENTIVE_GROUP_TYPE) {
    const payload: any[] = [];
    quoteData.hotels.forEach((item: any) => {
      const hotelData: any = [];
      item.hotels.forEach((hotel: any) => {
        let hotelCodes: string = '';
        const roomCodes: any[] = [];
        if (hotel.hCode) {
          hotelCodes = hotel.hCode;
        }
        hotel?.rooms?.forEach((room: any) => {
          if (room.code) {
            roomCodes.push(room.code);
          }
        });
        hotelData.push({
          hotelCodes: hotelCodes,
          roomCodes: roomCodes
        });
      });
      payload.push({
        brochureCode: item.brochure,
        destinationCode: item.destCode,
        hotelData: hotelData,
        numberOfRooms: item.numOfRooms
      });
    });
    isValid = await adminSetupStore.checkMaxRooms(payload);
  }
  return isValid;
};

const checkHotelInBlackoutDate = (hotelsData: any[]) => {
  return hotelsData.some((hotelOption: any) => {
    return !validateBlackoutDate(hotelOption.hotels);
  });
};

const renderHotelTemp = () => {
  const optionHotel: any = [];
  let roomsTemp: any = [];
  let hotelsTemp: any = [];

  hotels.value.forEach((hotelOption: any) => {
    hotelsTemp = [];
    hotelOption.hotels.forEach((hotel: any) => {
      roomsTemp = [];
      hotel.rooms.forEach((room: any) => {
        const roomForm = {
          room: renderRoomName(room.room, LANG_ENGLISH, false),
          roomFr: renderRoomName(room.room, LANG_FRENCH, false),
          longName: renderRoomName(room.room, LANG_ENGLISH, true),
          longNameFr: renderRoomName(room.room, LANG_FRENCH, true),
          code: room.room.room,
          totalTax: 0
        };
        roomsTemp.push(roomForm);
      });

      const hotelDetail = {
        hCode: hotel.hotel?.hCode,
        hotel: hotel.hotel?.name,
        hotelChain: hotel.hotel?.chainName,
        code: hotel.hotel?.code,
        hNote: '',
        adminMaxRoom: hotel.hotel?.maxRooms,
        rooms: roomsTemp,
        inBlackoutDate: hotel.hotel?.inBlackoutDate
      };

      hotelsTemp.push(hotelDetail);
    });
    const hotelForm = {
      airportCode: hotelOption.destination.airportCode,
      dest: getTextVariant(currentLanguage, `${hotelOption.destination.name} (${hotelOption.destination.country})`, `${hotelOption.destination.nameFr} (${hotelOption.destination.countryFr})`),
      destCode: hotelOption.destination.code,
      adt: +hotelOption.adults,
      chd: +hotelOption.children,
      numOfRooms: +hotelOption.numOfRooms,
      depDate: formatDate(hotelOption.dateRange[0], 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      retDate: formatDate(hotelOption.dateRange[1], 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      duration: hotelOption.duration,
      hNote: '',
      hotels: hotelsTemp,
      additionalNote: hotelOption.additionalNote,
      brochure: hotelOption.brochure
    };
    optionHotel.push(hotelForm);
  });

  return optionHotel;
};

const handleSubmit = async () => {
  await emits('submit');
  isShowError.value = true;
  cleanEmptyRooms(hotels.value);
  v$.value.$validate();
  const hotelsTemp = renderHotelTemp();
  if (!v$.value.$error && props.isValidation) {
    if ((checkPassengerHotels(hotelsTemp) || checkNumberOfRoomsHotels(hotelsTemp)) && !isWorksheetRequote.value) {
      const dialogRef = dialog.open(ValidateQuoteFormNotification, {
        data: {
          message: t('advance_quote.form.min_pax_package', { minPax: hotelMinimumConfig.value.minPax, minRoom: hotelMinimumConfig.value.minRoomsCabins })
        },
        props: {
          header: t('advance_quote.form.notice'),
          modal: true
        },
        templates: {
          footer: () => {
            return [
              h(
                'div',
                { class: 'text-center' },
                h(Button, {
                  label: t('advance_quote.form.return_to_form'),
                  autofocus: true,
                  onClick: () => dialogRef.close()
                })
              )
            ];
          }
        }
      });
      return;
    }

    const agentInfo = cloneDeep(renderAgentInfo(isWorksheetRequote.value, isClearAgencyInfo.value));
    const payload = {
      qType: HOTEL_ONLY,
      ...agentInfo,
      lang: props.selectedLanguage,
      advanced: true,
      isReplicate: isWorksheetRequote.value,
      hotels: hotelsTemp,
      avgPaxRoom: props.avgPaxRoomData,
      originalQuote: renderOriginQuote(true, quote.value?.sk),
      ...getCommonPayload(),
      insta: hotelInstaStatus.value.every((status) => status === STATUS_OPEN) ? STATUS_OPEN : STATUS_CLOSED
    };

    const checkOverDateHotel = hotelsTemp.some((item: any) => validateDateTime(item?.depDate, props.advancedForm[0].groupType));

    if (checkOverDateHotel) {
      showModalErrorHotel();
    } else if (checkHotelInBlackoutDate(hotelsTemp)) {
      showValidateQuoteFormNotificationHotel(t('advance_quote.form.has_black_out_period'));
    } else {
      const isMaxRoomValid = await checkMaxRoomsHotel(payload);
      if (!isMaxRoomValid) {
        showValidateQuoteFormNotificationHotel(t('advance_quote.form.max_rooms_invalid'));
      } else {
        submitQuoteStore
          .handleSubmit(payload, undefined, openModalTimeOut, undefined)
          .then(() => {
            handleShowConfirmModal();
            removeHotels();
            removeRooms();
            isShowError.value = false;
            hotelStore.clearRoomCategory();
            viewQuoteStore.updateViewQuote({});
          })
          .catch((e) => {
            toast.add({ severity: 'error', summary: '', detail: e.message || t('notification.save_error'), life: 5000 });
          });
      }
    }
  }
};

const resetHotelData = (hotel: any) => {
  hotel.destination = '';
  hotel.dateRange = [];
  hotel.duration = '';
  hotel.adults = 0;
  hotel.children = 0;
  hotel.numOfRooms = 0;
  hotel.additionalNote = '';
};

const resetRoomData = (room: any) => {
  room.room = '';
};

const removeHotels = () => {
  hotelInstaStatus.value = [STATUS_CLOSED];
  hotels.value.forEach((listHotel: any) => {
    resetHotelData(listHotel);
    listHotel.hotels.forEach((hotel: any) => {
      hotel.hotel = '';
      hotel.rooms.forEach((room: any) => {
        resetRoomData(room);
      });
    });
  });

  if (hotels.value.length > 1) {
    hotels.value.splice(1);
  }
  viewQuoteStore.getAction('view');
  viewQuoteStore.getActionRequoteFromWorksheet(true);
};

const clearAllDataHotel = () => {
  removeHotels();
  isShowError.value = false;
  maxDateRangeValue.value[0] = isWorksheetRequote.value ? undefined : getLimitDate(props.advancedForm[0].groupTypeId, limitCalendar.value, 'endDateConfig');
  emits('clear');
  isClearAgencyInfo.value = true;
};

const removeRooms = () => {
  hotels.value.forEach((listHotel: any) => {
    listHotel.hotels.forEach((hotel: any) => {
      if (hotel.rooms.length > 1) {
        hotel.rooms.splice(1);
      }
    });
  });
};

const addOptionHotel = () => {
  hotels.value.push(initHotelForm());
  v$ = useVuelidate(rules, hotels.value);
  hotelInstaStatus.value?.push(STATUS_CLOSED);
  hotels.value.forEach((hotel: any, hotelIndex: number) => {
    if (hotelIndex && hotel.dateRange?.length == 0) {
      maxDateRangeValue.value[hotelIndex] = isWorksheetRequote.value ? undefined : getLimitDate(props.advancedForm[0].groupTypeId, limitCalendar.value, 'endDateConfig');
    }
  });
};

const removeOptionHotel = () => {
  hotels.value.pop();
  hotelInstaStatus.value?.pop();
  maxDateRangeValue.value?.pop();
  v$ = useVuelidate(rules, hotels.value);
};

const addHotel = (indexOption: number) => {
  hotels.value[indexOption].hotels.push(initHotelData());
  v$ = useVuelidate(rules, hotels.value);
};

const removeHotel = (indexOption: number) => {
  hotels.value[indexOption].hotels.pop();
  v$ = useVuelidate(rules, hotels.value);
};

const addRoom = (indexOption: number, indexHotel: number) => {
  hotels.value[indexOption].hotels[indexHotel].rooms.push({
    room: ''
  });
  v$ = useVuelidate(rules, hotels.value);
};

const removeRoom = (indexOption: number, indexHotel: number) => {
  hotels.value[indexOption].hotels[indexHotel].rooms.pop();
  v$ = useVuelidate(rules, hotels.value);
};

const handleShowConfirmModal = () => {
  dialog.open(SubmitNotificationModal, {
    props: {
      header: t('advance_quote.confirm_quote_id'),
      modal: true
    }
  });
};

const renderQuoteTabIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-term', innerHTML: props.quoteIntro.termsFr });
  } else {
    return h('div', { class: 'english-term', innerHTML: props.quoteIntro.terms });
  }
};

const handleDateLimitationHotel = (advancedFormHotel: any) => {
  if (advancedFormHotel) {
    minDateRangeValue.value = isWorksheetRequote.value ? new Date(DateTime.utc().plus({ days: 1 })) : getLimitDate(advancedFormHotel, limitCalendar.value, 'startDateConfig');
    maxDateRangeValue.value[0] = isWorksheetRequote.value ? undefined : getLimitDate(advancedFormHotel, limitCalendar.value, 'endDateConfig');
  }
};

const updateMaxDayRange = (value: any, optionIndex: number) => {
  const checkRequote = isWorksheetRequote.value ? undefined : getLimitDate(props.advancedForm[0].groupTypeId, limitCalendar.value, 'endDateConfig');
  maxDateRangeValue.value[optionIndex] = value ?? checkRequote;
};

watch(
  () => hotels.value,
  (newValue) => {
    newValue.forEach((hotelVal) => {
      if (!hotelVal.adults) {
        hotelVal.adults = 0;
      }
      if (!hotelVal.children) {
        hotelVal.children = 0;
      }
      if (!hotelVal.numOfRooms) {
        hotelVal.numOfRooms = 0;
      }
    });
  },
  { deep: true }
);

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      hotels.value.forEach((item: any) => {
        item.dateRange = [];
        item.duration = '';
        if (item.hotels.length) {
          item.hotels.forEach((hotel: any) => {
            hotel.hotel = '';
            hotel.rooms.forEach((room: any) => {
              room.room = '';
            });
          });
        }
      });
      handleDateLimitationHotel(newValue[0].groupTypeId);
    }
  },
  { deep: true }
);

watch(
  () => props.calendarData,
  (newValue) => {
    if (newValue) {
      limitCalendar.value = getQuoteDepartureData(newValue);
    }
  },
  { deep: true }
);

watch(
  () => hotels.value[0].dateRange,
  (newValue) => {
    if (!newValue) {
      maxDateRangeValue.value[0] = isWorksheetRequote.value ? undefined : getLimitDate(props.advancedForm[0].groupTypeId, limitCalendar.value, 'endDateConfig');
    }
  },
  { deep: true }
);

onMounted(() => {
  if (quote.value.hotels && destinationsOnlyHotel.value) {
    hotelInstaStatus.value = [];
    quote.value.hotels.forEach(async (hotelItem: any, index: number) => {
      if (index) {
        hotels.value.push(initHotelForm());
      }
      if (isRequote.value) {
        await destinationStore.getDestinationRequote(hotelItem.destCode);
        await destinationStore.getDurationRequoteByDestination(hotelItem.duration, destinationRequote.value[0]);
        hotels.value[index].destination = destinationStore.selectDestinationRequote[0];
        hotels.value[index].adults = hotelItem.adt;
        hotels.value[index].children = hotelItem.chd;
        hotels.value[index].brochure = hotelItem.brochure;
        hotels.value[index].numOfRooms = hotelItem.numOfRooms;
        hotels.value[index].additionalNote = hotelItem.additionalNote;
        hotels.value[index].hotels = [];
        hotelItem.hotels.forEach((hotel: any, hotelIndex: number) => {
          hotels.value[index].hotels.push({
            hotel: '',
            rooms: []
          });
          hotel.rooms.forEach(() => {
            hotels.value[index].hotels[hotelIndex].rooms.push({
              room: ''
            });
          });
        });
      }
    });
  }
});
</script>
<template>
  <div class="hotel-tab">
    <LoadingSpinner v-if="isLoading" />
    <div class="mb-3" v-if="quoteIntro">
      <renderQuoteTabIntroHtml></renderQuoteTabIntroHtml>
    </div>
    <HotelFormOption
      :instanceForm="instanceForm"
      :advancedForm="advancedForm"
      :calendarData="calendarData"
      v-model:hotels="hotels"
      :validate="v$.$each.$response.$errors"
      :minDateRangeValue="minDateRangeValue"
      :maxDateRangeValue="maxDateRangeValue"
      :isShowError="isShowError"
      @addOptionHotel="addOptionHotel()"
      @removeOptionHotel="removeOptionHotel()"
      @addRoom="(indexOption, indexHotel) => addRoom(indexOption, indexHotel)"
      @removeRoom="(indexOption, indexHotel) => removeRoom(indexOption, indexHotel)"
      @addHotel="(indexOption) => addHotel(indexOption)"
      @removeHotel="(indexOption) => removeHotel(indexOption)"
      @updateMaxDayRange="(value, optionIndex) => updateMaxDayRange(value, optionIndex)"
    />
    <div class="flex gap-3">
      <Button id="btn-submit-form" :label="$t('advance_quote.form.submit')" class="mt-2 bg-primary border-noround" @click="handleSubmit()"> </Button>
      <Button id="btn-clear-form" :label="$t('common.buttons.clear')" class="mt-2 bg-primary border-noround" @click="clearAllDataHotel()"> </Button>
    </div>
    <p class="mt-3" v-if="!isAdvancedquoteForm">
      {{ $t('advance_quote.form.for_more') }} <a href="#/external/search/hotel" class="text-primary">{{ $t('advance_quote.form.click_here') }}</a
      >.
    </p>
  </div>
</template>
